import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Box } from '@mui/material';
import { LegalPageWrapper, LegalContent, LegalDocumentTitle } from './LegalPage.styled';
import SEO from '../common/SEO/SEO';
import NkySpinner from '../common/NkySpinner/NkySpinner';

const DOCUMENT_MAP = {
  terms: {
    file: 'User Terms of Service.pdf',
    title: 'Terms of Service',
    path: '/terms',
    description:
      'Read the NKY Connect Terms of Service for using our platform to find and connect with local home service providers.',
  },
  'business-terms': {
    file: 'Business Terms of Service.pdf',
    title: 'Business Terms of Service',
    path: '/business-terms',
    description:
      'Read the NKY Connect Business Terms of Service for service providers listing on our platform.',
  },
  privacy: {
    file: 'Privacy Policy.pdf',
    title: 'Privacy Policy',
    path: '/privacy',
    description:
      'Learn how NKY Connect collects, uses, and protects your personal information.',
  },
  'sms-terms': {
    file: 'NKY_Connect_SMS_Terms.pdf',
    title: 'SMS Terms & Conditions',
    path: '/sms-terms',
    description:
      'Read the NKY Connect SMS Terms & Conditions for text message notifications.',
  },
};

const LegalPage = ({ documentSlug }) => {
  const [pdfSrc, setPdfSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = DOCUMENT_MAP[documentSlug];

  useEffect(() => {
    if (!config) {
      setError('Document not found');
      setLoading(false);
      return;
    }

    const base = process.env.PUBLIC_URL || '';
    const url = `${base}/legal/${encodeURIComponent(config.file).replace(/%2F/g, '/')}`;
    const pdfUrl = `${url}#navpanes=0&zoom=100`;

    fetch(url, { method: 'HEAD' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load document');
        setPdfSrc(pdfUrl);
        setError(null);
      })
      .catch(() => setError('Unable to load this page.'))
      .finally(() => setLoading(false));
  }, [documentSlug, config]);

  const seo = config ? (
    <SEO
      title={config.title}
      description={config.description}
      path={config.path}
    />
  ) : (
    <SEO title="Page not found" description="Legal document not found." noindex />
  );

  if (!config) {
    return (
      <LegalPageWrapper>
        {seo}
        <Typography color="textSecondary">Page not found.</Typography>
        <Link to="/">Return home</Link>
      </LegalPageWrapper>
    );
  }

  if (loading) {
    return (
      <LegalPageWrapper>
        {seo}
        <Box display="flex" justifyContent="center" py={4}>
          <NkySpinner />
        </Box>
      </LegalPageWrapper>
    );
  }

  if (error) {
    return (
      <LegalPageWrapper>
        {seo}
        <Typography color="error">{error}</Typography>
        <Link to="/">Return home</Link>
      </LegalPageWrapper>
    );
  }

  return (
    <LegalPageWrapper $isPdf>
      {seo}
      <LegalDocumentTitle>{config.title}</LegalDocumentTitle>
      <LegalContent>
        <Box>
          <Box
            component="iframe"
            src={pdfSrc}
            title={config.title}
            sx={{
              width: '100%',
              height: '88vh',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          />
          <Typography variant="body2" sx={{ mt: 1 }}>
            If the PDF does not load,{' '}
            <a href={pdfSrc.split('#')[0]} target="_blank" rel="noopener noreferrer">
              open it in a new tab
            </a>
            .
          </Typography>
        </Box>
      </LegalContent>
    </LegalPageWrapper>
  );
};

export default LegalPage;
